import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const useTableExport = () => {
  // Función para copiar datos al portapapeles
  const copyToClipboard = useCallback(async (data, columns) => {
    try {
      // Crear el texto en formato tabla
      const headers = columns.map(col => col.header).join('\t');
      const rows = data.map(row => 
        columns.map(col => {
          const value = row[col.accessorKey] || '';
          return String(value).replace(/\t/g, ' '); // Reemplazar tabs por espacios
        }).join('\t')
      );
      
      const tableText = [headers, ...rows].join('\n');
      
      // Usar la API moderna del portapapeles si está disponible
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(tableText);
      } else {
        // Fallback para navegadores más antiguos
        const textArea = document.createElement('textarea');
        textArea.value = tableText;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      return { success: true, message: 'Datos copiados al portapapeles' };
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error);
      return { success: false, message: 'Error al copiar los datos' };
    }
  }, []);

  // Función para exportar a Excel
  const exportToExcel = useCallback((data, columns, filename = 'tabla_datos') => {
    try {
      // Crear los datos en formato Excel
      const excelData = data.map(row => {
        const excelRow = {};
        columns.forEach(col => {
          excelRow[col.header] = row[col.accessorKey] || '';
        });
        return excelRow;
      });

      // Crear el workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Datos');

      // Ajustar el ancho de las columnas
      const colWidths = columns.map(col => ({
        wch: Math.max(col.header.length, 15) // Mínimo 15 caracteres
      }));
      ws['!cols'] = colWidths;

      // Descargar el archivo
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      saveAs(blob, `${filename}.xlsx`);
      
      return { success: true, message: 'Archivo Excel descargado exitosamente' };
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      return { success: false, message: 'Error al generar el archivo Excel' };
    }
  }, []);

  // Función para exportar a CSV
  const exportToCSV = useCallback((data, columns, filename = 'tabla_datos') => {
    try {
      // Crear los headers
      const headers = columns.map(col => `"${col.header}"`).join(',');
      
      // Crear las filas
      const rows = data.map(row => 
        columns.map(col => {
          const value = row[col.accessorKey] || '';
          // Escapar comillas dobles y envolver en comillas
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      );
      
      const csvContent = [headers, ...rows].join('\n');
      
      // Agregar BOM para compatibilidad con caracteres especiales
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { 
        type: 'text/csv;charset=utf-8' 
      });
      
      saveAs(blob, `${filename}.csv`);
      
      return { success: true, message: 'Archivo CSV descargado exitosamente' };
    } catch (error) {
      console.error('Error al exportar a CSV:', error);
      return { success: false, message: 'Error al generar el archivo CSV' };
    }
  }, []);

  // Función para exportar a PDF
  const exportToPDF = useCallback((data, columns, filename = 'tabla_datos', title = 'Reporte de Datos') => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // Orientación horizontal
      
      // Configurar título
      doc.setFontSize(16);
      doc.text(title, 15, 20);
      
      // Agregar fecha
      doc.setFontSize(10);
      doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 15, 30);
      
      // Preparar datos para la tabla
      const tableHeaders = columns.map(col => col.header);
      const tableData = data.map(row => 
        columns.map(col => String(row[col.accessorKey] || ''))
      );
      
      // Intentar usar autoTable, si no está disponible usar método básico
      if (typeof doc.autoTable === 'function') {
        // Configurar la tabla usando doc.autoTable
        doc.autoTable({
          head: [tableHeaders],
          body: tableData,
          startY: 40,
          styles: { 
            fontSize: 8,
            cellPadding: 2
          },
          headStyles: { 
            fillColor: [63, 81, 181], // Color azul
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: { 
            fillColor: [245, 245, 245] 
          },
          margin: { top: 40, left: 15, right: 15 },
          theme: 'striped'
        });
      } else {
        // Método alternativo básico
        let yPosition = 50;
        
        // Headers
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        let xPosition = 15;
        const colWidth = (297 - 30) / tableHeaders.length; // A4 horizontal minus margins
        
        tableHeaders.forEach((header, index) => {
          doc.text(header, xPosition + (index * colWidth), yPosition);
        });
        
        yPosition += 10;
        
        // Data rows
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        
        tableData.forEach((row, rowIndex) => {
          if (yPosition > 200) { // Nueva página si se acaba el espacio
            doc.addPage();
            yPosition = 20;
          }
          
          row.forEach((cell, cellIndex) => {
            const cellText = String(cell).substring(0, 20); // Limitar longitud
            doc.text(cellText, xPosition + (cellIndex * colWidth), yPosition);
          });
          
          yPosition += 7;
        });
      }
      
      // Descargar el PDF
      doc.save(`${filename}.pdf`);
      
      return { success: true, message: 'Archivo PDF descargado exitosamente' };
    } catch (error) {
      console.error('❌ Error al exportar a PDF:', error);
      return { success: false, message: 'Error al generar el archivo PDF' };
    }
  }, []);

  // Función para obtener datos filtrados de la tabla (considerando filtros y búsqueda)
  const getFilteredData = useCallback((table) => {
    // Obtener los datos filtrados (después de búsqueda pero antes de paginación)
    const filteredRowModel = table.getFilteredRowModel();
    return filteredRowModel.rows.map(row => row.original);
  }, []);

  // Función principal que maneja todos los tipos de exportación
  const handleExport = useCallback(async (type, table, columns, options = {}) => {
    const {
      filename = 'datos_tabla',
      title = 'Reporte de Datos',
      useFilteredData = true
    } = options;

    try {
      // Obtener los datos (filtrados o todos)
      const data = useFilteredData ? getFilteredData(table) : table.getCoreRowModel().rows.map(row => row.original);
      
      if (data.length === 0) {
        return { success: false, message: 'No hay datos para exportar' };
      }

      switch (type) {
        case 'copy':
          return await copyToClipboard(data, columns);
        case 'excel':
          return exportToExcel(data, columns, filename);
        case 'csv':
          return exportToCSV(data, columns, filename);
        case 'pdf':
          return exportToPDF(data, columns, filename, title);
        default:
          return { success: false, message: 'Tipo de exportación no válido' };
      }
    } catch (error) {
      console.error('Error en handleExport:', error);
      return { success: false, message: 'Error al procesar la exportación' };
    }
  }, [copyToClipboard, exportToExcel, exportToCSV, exportToPDF, getFilteredData]);

  return {
    handleExport,
    copyToClipboard,
    exportToExcel,
    exportToCSV,
    exportToPDF,
    getFilteredData
  };
};

export default useTableExport; 